import { Box, Button, Link, Stack } from '@mui/material';
import { DataGridProProps } from '@mui/x-data-grid-pro';
import { t } from 'i18next';
import { BlankCell } from '../../../common/components/Cells/BlankCell';
import { ButtonLinkCell } from '../../../common/components/Cells/ButtonLinkCell';
import { LoadingCell } from '../../../common/components/Cells/LoadingCell';
import { MenuCell, MenuCellAction } from '../../../common/components/Cells/MenuCell';
import { TagCell } from '../../../common/components/Cells/TagCell';
import { FilterValueInputType } from '../../../common/components/FilterPanel/enum/FilterValueInputType';
import { Operation } from '../../../common/components/FilterPanel/enum/Operation';
import { FilterConfiguration } from '../../../common/components/FilterPanel/types';
import { TypographyOverflow } from '../../../common/components/TypographyOverflow';
import { PaginatedTableController, PaginatedTableState } from '../../../common/controllers/PaginatedTableController';
import { TraceType, getTraceTypeLabel } from '../../../common/enums/TraceType';
import { WithRouterProps, withRouter } from '../../../common/higher-order-components/with-router';
import { FieldConfiguration } from '../../../common/types/paginated-table';
import { BLANK_PAGINATION_RESULT, CursorInfo, PaginationResult } from '../../../common/types/pagination';
import { DateUtils } from '../../../common/utils/DateUtils';
import { UrlHelper } from '../../../common/utils/UrlHelper';
import { stopPropagationHandler } from '../../../common/utils/handlers';
import { SlotType } from '../../../modal-flows/models/SlotType';
import { PrefillSlot } from '../../../modal-flows/models/valueObjects/PrefillSlot';
import { ModalManager } from '../../../modal-flows/services/ModalManager';
import { SlotService } from '../../../modal-flows/services/SlotService';
import { PATHS } from '../../../routing/routes';
import { Slot } from '../models/Slot';
import { ReportStatus, getReportStatusLabel } from '../models/valueObjects/Report';
import { SlotRepository } from '../services/ProjectSlotsRepository';
import { OrganizationDataProvider } from '../../../organization/dashboard/providers/OrganizationDataProvider';
import { Preset } from '../../../common/models/valueObjects/Preset';

type Props = WithRouterProps & {};

type State = PaginatedTableState & {
  canCreateSlot: boolean;
  canUploadTrace: boolean;
  canDownloadTracers: boolean;
};

export class Controller extends PaginatedTableController<Slot, Props, State> {
  private readonly modalManager = new ModalManager();
  private readonly repository = new SlotRepository();
  private readonly slotService = new SlotService();
  private readonly organizationDataProvider = new OrganizationDataProvider();

  private projectId: number;

  private slotTypes: SlotType[];
  private availableTags: Preset[];

  constructor(props: Props, context: unknown) {
    super(props, context);

    this.slotTypes = [];
    this.availableTags = [];

    ({ projectId: this.projectId } = this.parseNumericIds(['projectId']));
  }

  getBaseState(): State {
    return {
      ...super.getBaseState(),
      canCreateSlot: false,
      canUploadTrace: false,
      canDownloadTracers: false,
    };
  }

  componentDidMount() {
    super.componentDidMount();

    this.load();
  }

  componentWillUnmount() {
    super.componentWillUnmount();

    this.slotService.cancelAllRequests();
    this.repository.cancelAllRequests();
    this.organizationDataProvider.cancelAllRequests();
  }

  protected doInitialDataFetch(): void {
    // do nothing until permissions are fetched
  }

  protected renderRightToolbarElements(): React.ReactElement | null {
    const { canDownloadTracers, canCreateSlot } = this.state;

    if (!canDownloadTracers && !canCreateSlot) {
      return null;
    }

    const output = [];

    if (canDownloadTracers) {
      const buttonProps = { href: PATHS.SENSORS, target: '_blank', 'aria-label': t('sensors') };
      output.push(
        <Button key="tracersAndScanners" variant="outlined" size={'small'} color="primary" {...buttonProps}>
          {t('sensors')}
        </Button>
      );
    }

    if (canCreateSlot) {
      output.push(
        <Button key="createNewSlot" onClick={() => this.handleCreateNewSlot()} variant="contained" size={'small'} color="primary">
          {t('createNewSlot')}
        </Button>
      );
    }

    return (
      <Stack direction="row" alignItems="center" gap={2}>
        {output}
      </Stack>
    );
  }

  protected isItemClickable(item: Slot): boolean {
    return !!item.getLatestReport();
  }

  protected onItemClick(item: Slot) {
    const report = item.getLatestReport();
    if (report) {
      this.navigate(UrlHelper.generatePath(PATHS.REPORT, { reportId: report.getId().toString() }));
    }
  }

  protected async fetchData(cursorInfo: CursorInfo): Promise<PaginationResult<Slot>> {
    try {
      const response = await this.repository.getSlotsForProject(this.projectId, cursorInfo, this.getFilters(), this.getSorters());

      // prefetch permissions
      const slotIds = response.data.map((slot) => slot.getId());
      await this.permissionsService.bulkPrefetchSlotPermissions(slotIds);

      return response;
    } catch (e: unknown) {
      this.handleError(e, { invalidUrl: t('nonExistingProject') });
      return BLANK_PAGINATION_RESULT;
    }
  }

  protected getFieldConfigurations(): FieldConfiguration<Slot>[] {
    const permissions = this.state;
    const output: FieldConfiguration<Slot>[] = [
      {
        fieldKey: 'name',
        label: t('slotName'),
        description: t('slotName'),
        sortable: true,
        render: (item, { hasFocus }) => (
          <TypographyOverflow hasFocus={hasFocus} alwaysOnTooltip={item.getDescription()}>
            {item.getName()}
          </TypographyOverflow>
        ),
      },
      {
        fieldKey: 'reportName',
        label: t('reportName'),
        description: t('reportName'),
        sortable: false,
        render: (item, { tabIndex, hasFocus }) => {
          const report = item.getLatestReport();
          if (report) {
            return (
              <Link tabIndex={tabIndex} href={UrlHelper.generatePath(PATHS.REPORT, { reportId: report.getId().toString() })} onClick={stopPropagationHandler}>
                <TypographyOverflow hasFocus={hasFocus}>{report.getName()}</TypographyOverflow>
              </Link>
            );
          }
          // Slot is empty, there is no report
          if (!permissions.canUploadTrace) {
            // The user cannot create a new report
            return <BlankCell blankText={t('noReportInSlot')} />;
          }
          // The user can create a report
          return (
            <ButtonLinkCell
              tabIndex={tabIndex}
              aria-label={t('a11y.project.slots.addNewTrace', { slotName: item.getName() })}
              onClick={(event) => {
                event.stopPropagation();
                this.handleUploadNewTrace(item.getId());
              }}
            >
              {t('add')}
            </ButtonLinkCell>
          );
        },
      },
      {
        fieldKey: 'type',
        label: t('type'),
        description: t('type'),
        sortable: true,
        gqlKeys: {
          sorterField: 'traceType',
        },
        columnProps: {
          maxWidth: 150,
        },
        render: (item, { hasFocus }) => <TypographyOverflow hasFocus={hasFocus}>{getTraceTypeLabel(item.getSlotType())}</TypographyOverflow>,
      },
      {
        fieldKey: 'traceName',
        label: t('trace'),
        description: t('trace'),
        sortable: true,
        gqlKeys: {
          sorterField: 'lastReportTraceName',
        },
        render: (item, { tabIndex, hasFocus }) => {
          const report = item.getLatestReport();
          if (report === null) {
            // The slot is empty, there is no trace
            if (!permissions.canUploadTrace) {
              // The user cannot upload a new trace
              return <BlankCell blankText={t('noTraceInSlot')} />;
            }
            // The user can upload a new trace
            return (
              <ButtonLinkCell
                tabIndex={tabIndex}
                aria-label={t('a11y.project.slots.addNewTrace')}
                onClick={(event) => {
                  event.stopPropagation();
                  this.handleUploadNewTrace(item.getId());
                }}
              >
                {t('add')}
              </ButtonLinkCell>
            );
          }
          const trace = report.getTrace();
          if (trace === null) {
            // The latest report has no trace
            return <BlankCell blankText={t('noTraceInReport')} tooltip={t('noTraceInReportTooltip')} />;
          }
          return <TypographyOverflow hasFocus={hasFocus}>{trace.getName()}</TypographyOverflow>;
        },
      },
      {
        fieldKey: 'lastAnalysis',
        label: t('lastAnalysis'),
        description: t('lastAnalysis'),
        sortable: true,
        gqlKeys: {
          sorterField: 'lastReportCreatedAt',
        },
        columnProps: {
          maxWidth: 125,
        },
        render: (item, { hasFocus }) => {
          const report = item.getLatestReport();
          if (report === null) {
            return <BlankCell blankText={t('noLastAnalysisInSlot')} />;
          }
          return <TypographyOverflow hasFocus={hasFocus}>{DateUtils.toDateString(report.getCreatedAt())}</TypographyOverflow>;
        },
      },
      {
        fieldKey: 'reportCount',
        label: t('versions'),
        description: t('versions'),
        sortable: true,
        columnProps: {
          maxWidth: 100,
        },
        render: (item) => item.getReportCount(),
      },
      {
        fieldKey: 'tags',
        label: t('tags'),
        description: t('tags'),
        sortable: false,
        columnProps: {
          maxWidth: 250,
        },
        render: (item, { tabIndex }) => {
          if (item.getTags().length > 0) {
            return <TagCell tags={item.getTags()} />;
          }

          return (
            <LoadingCell
              permissionCallback={() => this.permissionsService.canUpdateSlot(item.getId())}
              noPermission={<BlankCell blankText={t('noTagsInSlot')} />}
              withPermission={
                <ButtonLinkCell
                  tabIndex={tabIndex}
                  aria-label={t('a11y.project.slots.updateTags', { slotName: item.getName() })}
                  onClick={(event) => {
                    event.stopPropagation();
                    this.handleUpdateSlot(item);
                  }}
                >
                  {t('add')}
                </ButtonLinkCell>
              }
            />
          );
        },
      },
      {
        fieldKey: 'profile',
        label: t('profile'),
        description: t('profile'),
        sortable: false,
        render: (item, { tabIndex, hasFocus }) => {
          const report = item.getLatestReport();
          if (report === null) {
            // The slot is empty, there is no profile
            return <BlankCell blankText={t('noProfileInSlot')} />;
          }
          const profile = report.getProfile();
          if (profile === null) {
            // The latest report has no explicit profile
            return <BlankCell blankText={t('noProfileInReport')} tooltip={t('noProfileInReportTooltip')} />;
          }
          // The latest report has a profile
          return (
            <Link
              tabIndex={tabIndex}
              onClick={stopPropagationHandler}
              href={UrlHelper.generatePath(PATHS.PROFILE_DETAIL, { profileId: profile.getId().toString() })}
            >
              <TypographyOverflow hasFocus={hasFocus}>{profile.getLabel()}</TypographyOverflow>
            </Link>
          );
        },
      },
      {
        fieldKey: 'status',
        label: t('status'),
        sortable: true,
        gqlKeys: {
          sorterField: 'lastReportState',
        },
        columnProps: {
          maxWidth: 100,
        },
        render: (item) => {
          const status = item.getLatestReport()?.getStatus();
          const label = getReportStatusLabel(status);
          switch (status) {
            case ReportStatus.DONE:
              return label;
            case ReportStatus.PENDING:
              return <Box sx={{ color: 'grey.600' }}>{label}</Box>;
            case ReportStatus.FAILED:
              return <Box sx={{ color: 'error.dark' }}>{label}</Box>;
            default:
              return <BlankCell blankText={label} />;
          }
        },
      },
    ];

    if (permissions.canUploadTrace) {
      output.push({
        fieldKey: 'updateTraceButton',
        label: '',
        description: t('uploadTraceButton'),
        sortable: false,
        columnProps: {
          maxWidth: 175,
          align: 'right',
          cellClassName: 'px-0',
        },
        render: (item, { tabIndex }) => {
          return (
            <Button
              tabIndex={tabIndex}
              variant="outlined"
              onClick={(event) => {
                event.stopPropagation();
                this.handleUploadNewTrace(item.getId());
              }}
            >
              <TypographyOverflow variant="button">{t('uploadTraceButton')}</TypographyOverflow>
            </Button>
          );
        },
      });
    }

    output.push({
      fieldKey: 'actions',
      label: '',
      description: t('a11y.project.slots.actions'),
      sortable: false,
      columnProps: {
        maxWidth: 60,
        minWidth: 60,
      },
      render: (slot, { tabIndex }) => {
        const actions: MenuCellAction[] = [
          {
            label: t('edit'),
            isActive: () => this.permissionsService.canUpdateSlot(slot.getId()),
            handler: () => this.handleUpdateSlot(slot),
          },
          {
            label: t('delete'),
            isActive: () => this.permissionsService.canDeleteSlot(slot.getId()),
            handler: () => this.handleDeleteSlot(slot.getId(), slot.getName()),
          },
        ];
        return <MenuCell tabIndex={tabIndex} actions={actions} />;
      },
    });

    return output;
  }

  protected shouldStoreFiltersInUrl(): boolean {
    return true;
  }

  protected getFilterConfigurations(): FilterConfiguration[] {
    return [
      {
        filterKey: 'name',
        label: t('slotName'),
        type: FilterValueInputType.STRING,
        operations: [
          { value: Operation.CONTAINS, label: t('filterLabels.contains') },
          { value: Operation.EQ, label: t('filterLabels.is') },
        ],
      },
      {
        filterKey: 'traceType',
        label: t('type'),
        type: FilterValueInputType.SELECT,
        operations: [
          { value: Operation.EQ, label: t('filterLabels.is') },
          { value: Operation.NEQ, label: t('filterLabels.isNot') },
        ],
        options: Object.values(TraceType).map((v) => ({ value: v, label: getTraceTypeLabel(v) })),
      },
      {
        filterKey: 'tags',
        label: t('tags'),
        type: FilterValueInputType.AUTOCOMPLETE,
        options: this.availableTags.map((tag) => tag.toFilterFormat()),
        operations: [{ value: Operation.CONTAINS, label: t('filterLabels.has') }],
      },
      {
        filterKey: 'lastReportTraceName',
        label: t('trace'),
        type: FilterValueInputType.STRING,
        operations: [
          { value: Operation.CONTAINS, label: t('filterLabels.contains') },
          { value: Operation.EQ, label: t('filterLabels.is') },
        ],
      },
      {
        filterKey: 'lastReportCreatedAt',
        label: t('lastAnalysis'),
        type: FilterValueInputType.DATE_TIME,
        operations: [
          { value: Operation.LT, label: t('filterLabels.isBefore') },
          { value: Operation.LE, label: t('filterLabels.isBeforeOrEqual') },
          { value: Operation.EQ, label: t('filterLabels.is') },
          { value: Operation.GE, label: t('filterLabels.isAfterOrEqual') },
          { value: Operation.GT, label: t('filterLabels.isAfter') },
        ],
      },
      {
        filterKey: 'lastReportState',
        label: t('status'),
        type: FilterValueInputType.SELECT,
        options: Object.values(ReportStatus).map((value) => ({ value, label: getReportStatusLabel(value) })),
        operations: [{ value: Operation.EQ, label: t('filterLabels.is') }],
      },
    ];
  }

  protected getAdditionalDataGridProps(): Partial<DataGridProProps<Slot>> {
    return {
      ...super.getAdditionalDataGridProps(),
      getRowId: (model) => {
        // Include information that can change at runtime in the MUI row ID
        // Slot.getLatestReport() can be omitted, since a new report will
        // also be reflected by a change in Slot.getReportCount()
        const id = model.getId();
        const name = model.getName();
        const description = model.getDescription();
        const reportCount = model.getReportCount();
        const tags = model.getTags();
        return `${id}_${name}_${description}_${reportCount}_${tags.join('')}`;
      },
    };
  }

  private async load() {
    try {
      const [slotTypes, availableTags, canCreateSlot, canUploadTrace, canDownloadTracers] = await Promise.all([
        this.slotService.getSlotTypes(),
        this.organizationDataProvider.getTags('', [], 100_000),
        this.permissionsService.canCreateSlot(this.projectId),
        this.permissionsService.canUploadTrace(this.projectId),
        this.permissionsService.canDownloadTracers(),
      ]);
      this.slotTypes = slotTypes;
      this.availableTags = availableTags;

      this.setState({ canCreateSlot, canUploadTrace, canDownloadTracers }, () => {
        super.doInitialDataFetch();
      });
    } catch (e) {
      this.handleError(e, undefined, true);
      // Handle the error silently and still load the table
      super.doInitialDataFetch();
    }
  }

  private async handleCreateNewSlot() {
    const { dismissed, slotId, reportId } = await this.modalManager.createSlot(this.projectId, this.slotTypes);

    // Slot was not created, do nothing
    if (!slotId) return;

    // A report generated, navigate to it
    if (reportId && !dismissed) {
      this.navigate(UrlHelper.generatePath(PATHS.REPORT, { reportId: reportId.toString() }));
      return;
    }

    // At least one slot was created, reload the table
    this.reloadTableContent();
  }

  private async handleUploadNewTrace(id: number) {
    const { dismissed, slotId, reportId } = await this.modalManager.createReport(this.projectId, this.slotTypes, id);

    // Slots were not modified, do nothing
    if (!slotId) return;

    // A report was generated, navigate to it
    if (reportId && !dismissed) {
      this.navigate(UrlHelper.generatePath(PATHS.REPORT, { reportId: reportId.toString() }));
      return;
    }

    // A slot may have been created, reload the table
    this.reloadTableContent();
  }

  private async handleUpdateSlot(slot: Slot) {
    const prefillSlotType = new SlotType(slot.getSlotType(), true);
    const prefillSlot = new PrefillSlot(slot.getId(), slot.getName(), slot.getDescription(), prefillSlotType, slot.getTags());
    const { slotId } = await this.modalManager.updateSlot(prefillSlot);

    // Slot was not updated, do nothing
    if (!slotId) return;

    // Slot was updated, reload the table
    this.reloadTableContent();

    // Clear the breadcrumb of all reports of this slot
    this.breadcrumbCachingService.clearSlot(slotId);
  }

  private async handleDeleteSlot(id: number, name: string) {
    const { slotId } = await this.modalManager.deleteSlot(id, name);

    // Slot was not deleted, do nothing
    if (!slotId) return;

    // Slot was deleted, reload the table
    this.reloadTableContent();

    // Clear the breadcrumb of all reports of this slot
    this.breadcrumbCachingService.clearSlot(slotId);
  }
}

export const ProjectSlotsController = withRouter(Controller);
